import React, { useState, useMemo, useEffect } from "react";
import { Form } from "react-bootstrap";
import DatePicker from "react-datepicker";

import { DetailsModal } from "components/grid/DetailsModal";

import { ControlSpinner } from "shared/components/spinners/ControlSpinner";

import { useFetchUserPageViews } from "./hooks/useFetchUserPageViews";
import { useFetchUserSessionRequests } from "./hooks/useFetchUserSessionRequests";
import { useFetchUserServerSessions } from "./hooks/useFetchUserServerSessions";
import { deriveSessionsFromPageViews } from "./utils/deriveSessionsFromPageViews";
import { deriveSessionsFromServerRequests } from "./utils/deriveSessionsFromServerRequests";
import { resolveAnalyticsMode } from "./utils/resolveAnalyticsMode";
import { computeAvgSessionMs } from "./utils/computeAvgSessionMs";
import { parseLastLoginDate } from "./utils/parseLastLoginDate";
import UserSummaryCards from "./components/UserSummaryCards";
import SessionTimeline from "./components/SessionTimeline";
import ServerSessionTimeline from "./components/ServerSessionTimeline";
import ServerOnlyBanner from "./components/ServerOnlyBanner";
import EventsFeed from "./components/EventsFeed";
import PagesVisited from "./components/PagesVisited";
import TruncationBanner from "./components/TruncationBanner";

import "react-datepicker/dist/react-datepicker.css";
import {
  ANALYTICS_MODE,
  CLIENT_TABS,
  SERVER_ONLY_TABS,
} from "./constants/ActivityAnalyticsConstants";
import "./ActivityAnalyticsDetailsModal.css";

function startOfDay(date) {
  return new Date(
    date.getFullYear(),
    date.getMonth(),
    date.getDate(),
    0,
    0,
    0,
    0
  );
}

function getInitialDateRange(lastLoginDate) {
  const parsed = parseLastLoginDate(lastLoginDate);
  const end = parsed ? parsed : new Date();
  const endDay = startOfDay(end);
  const startDay = new Date(endDay);
  startDay.setDate(startDay.getDate() - 7);
  return { startDate: startDay, endDate: endDay };
}

export default function ActivityAnalyticsDetailsModal(props) {
  const { show, onClose, user } = props;
  const [activeTab, setActiveTab] = useState("Sessions");

  const email = user ? user.contactEmail : "";

  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  useEffect(() => {
    if (user) {
      const { startDate: s, endDate: e } = getInitialDateRange(
        user.lastLoginDate
      );
      setStartDate(s);
      setEndDate(e);
    } else {
      setStartDate(null);
      setEndDate(null);
    }
  }, [user]);

  const { from, to } = useMemo(() => {
    if (!startDate || !endDate) return { from: null, to: null };
    const fromDay = startOfDay(startDate);
    const toDay = startOfDay(endDate);
    toDay.setDate(toDay.getDate() + 1);
    if (fromDay > toDay) return { from: null, to: null };
    return { from: fromDay.toISOString(), to: toDay.toISOString() };
  }, [startDate, endDate]);

  const {
    data: pageViewsPage,
    isLoading: pvLoading,
    isError: pvError,
    isSuccess: pvSuccess,
  } = useFetchUserPageViews(email, from, to);

  const {
    data: requestsPage,
    isLoading: rqLoading,
    isError: rqError,
  } = useFetchUserSessionRequests(email, from, to);

  const pageViews = useMemo(
    () => (pageViewsPage && pageViewsPage.items ? pageViewsPage.items : []),
    [pageViewsPage]
  );
  const requests  = requestsPage && requestsPage.items ? requestsPage.items : [];

  // Server logs are queried only when the page views query succeeded with no rows.
  const serverQueryEnabled =
    resolveAnalyticsMode({
      pageViewsSucceeded: pvSuccess,
      pageViewCount: pageViews.length,
    }) === ANALYTICS_MODE.SERVER_ONLY;

  const {
    data: serverPage,
    isError: srvError,
    isSuccess: srvSuccess,
  } = useFetchUserServerSessions(email, from, to, serverQueryEnabled);

  const serverRows = useMemo(
    () => (serverPage && serverPage.items ? serverPage.items : []),
    [serverPage]
  );
  const srvPending = serverQueryEnabled && !srvSuccess && !srvError;

  // An idle user (no page views and no server rows) keeps the normal empty state, not the banner.
  const mode =
    serverQueryEnabled && (srvPending || serverRows.length > 0)
      ? ANALYTICS_MODE.SERVER_ONLY
      : ANALYTICS_MODE.CLIENT;
  const isServerOnly = mode === ANALYTICS_MODE.SERVER_ONLY;
  const tabs = isServerOnly ? SERVER_ONLY_TABS : CLIENT_TABS;

  useEffect(() => {
    if (tabs.indexOf(activeTab) === -1) setActiveTab(tabs[0]);
  }, [tabs, activeTab]);
  const pageViewsTruncated = Boolean(pageViewsPage && pageViewsPage.truncated);
  const requestsTruncated  = isServerOnly
    ? Boolean(serverPage && serverPage.truncated)
    : Boolean(requestsPage && requestsPage.truncated);

  const clientSessions = useMemo(
    () => deriveSessionsFromPageViews(pageViews),
    [pageViews]
  );
  const serverSessions = useMemo(
    () => deriveSessionsFromServerRequests(serverRows),
    [serverRows]
  );
  const sessions = isServerOnly ? serverSessions : clientSessions;

  const avgSessionMs = useMemo(() => computeAvgSessionMs(sessions), [sessions]);

  const deviceMeta =
    !isServerOnly && clientSessions[0]
      ? [clientSessions[0].browser, clientSessions[0].os]
          .filter(Boolean)
          .join(" · ")
      : "";

  const eventRows = isServerOnly ? serverRows : requests;
  const totalEventCount = eventRows.reduce((sum, r) => sum + (r.count || 0), 0);

  const isLoading = pvLoading || rqLoading || srvPending;
  const isError = pvError || rqError || (serverQueryEnabled && srvError);

  const fullName =
    user && (user.firstName || user.lastName)
      ? `${user.firstName || ""} ${user.lastName || ""}`.trim()
      : "User";

  const displayRange =
    startDate && endDate
      ? startDate.toDateString() === endDate.toDateString()
        ? startDate.toLocaleDateString()
        : `${startDate.toLocaleDateString()} – ${endDate.toLocaleDateString()}`
      : null;

  return (
    <DetailsModal
      title={`User Analytics - ${fullName}`}
      show={show}
      handleClose={onClose}
    >
      <div className="activity-analytics-detail">
        {!user ? (
          <p className="text-muted">No user selected.</p>
        ) : (
          <React.Fragment>
            <div className="activity-date-range">
              <div className="activity-date-range__field">
                <Form.Label className="activity-date-range__label">
                  Start Date
                </Form.Label>
                <Form.Control
                  name="activityStartDate"
                  as={DatePicker}
                  selected={startDate}
                  onChange={(date) => setStartDate(date)}
                  maxDate={endDate || undefined}
                />
              </div>
              <div className="activity-date-range__field">
                <Form.Label className="activity-date-range__label">
                  End Date
                </Form.Label>
                <Form.Control
                  name="activityEndDate"
                  as={DatePicker}
                  selected={endDate}
                  onChange={(date) => setEndDate(date)}
                  minDate={startDate || undefined}
                />
              </div>
            </div>

            {!isError && (
              <React.Fragment>
                <div className="user-detail-header">
                  <div>
                    <strong>{email}</strong>
                    {displayRange && (
                      <span className="text-muted">
                        &nbsp;·&nbsp;Activity for {displayRange}
                      </span>
                    )}
                  </div>
                  {deviceMeta && (
                    <div className="text-muted" style={{ fontSize: "0.875rem" }}>
                      {deviceMeta}
                    </div>
                  )}
                </div>

                <UserSummaryCards
                  sessionCount={isLoading ? undefined : sessions.length}
                  eventCount={isLoading ? undefined : totalEventCount}
                  avgSessionMs={avgSessionMs}
                  showAvgSession={!isServerOnly}
                  eventLabel={isServerOnly ? "API Requests (est.)" : "API Requests"}
                />
              </React.Fragment>
            )}

            <div className="detail-tabs">
              {tabs.map((tab) => (
                <button
                  key={tab}
                  className={`detail-tab${
                    activeTab === tab ? " detail-tab--active" : ""
                  }`}
                  onClick={() => setActiveTab(tab)}
                >
                  {tab}
                </button>
              ))}
            </div>

            {isLoading && <ControlSpinner />}
            {isError && (
              <p className="text-danger">Failed to load activity data.</p>
            )}

            {!isLoading && !isError && (
              <React.Fragment>
                {isServerOnly && <ServerOnlyBanner />}
                <TruncationBanner
                  pageViewsTruncated={pageViewsTruncated}
                  requestsTruncated={requestsTruncated}
                />
                {activeTab === "Sessions" &&
                  (isServerOnly ? (
                    <ServerSessionTimeline sessions={serverSessions} />
                  ) : (
                    <SessionTimeline sessions={clientSessions} />
                  ))}
                {activeTab === "API Requests" && <EventsFeed requests={eventRows} />}
                {!isServerOnly && activeTab === "Pages" && (
                  <PagesVisited pageViews={pageViews} />
                )}
              </React.Fragment>
            )}
          </React.Fragment>
        )}
      </div>
    </DetailsModal>
  );
}
